// Shared Plotly styling for the dashboard charts.
// Plotly can't read CSS variables, so light / dark colours are resolved here in JS.
import { useColorMode } from '../../../components/providers/ColorModeProvider';

export const BRAND = "#142784";

// One colour per asset, shared by the energy-flow drawing and the charts.
// Mid-tone colours that read on both the light and dark surfaces
export const ASSET_COLORS = {
  grid: "#3B82F6",
  solar: "#10B981",
  battery: "#F59E0B",
  load: "#8B5CF6",
};

const CHART_COLORS = {
  light: {
    brand: BRAND,
    brandFill: "rgba(20, 39, 132, 0.08)",
    text: "#64748B",
    legend: "#334155",
    grid: "#F1F5F9",
    zeroLine: "#E2E8F0",
    hoverBg: "white",
    hoverBorder: "#E2E8F0",
    hoverText: "#0F172A",
    markerOutline: "white",
  },
  dark: {
    brand: "#8EA2FF",
    brandFill: "rgba(142, 162, 255, 0.12)",
    text: "#8A9BB3",
    legend: "#CBD5E1",
    grid: "#1E293B",
    zeroLine: "#273449",
    hoverBg: "#111827",
    hoverBorder: "#273449",
    hoverText: "#F1F5F9",
    markerOutline: "#111827",
  },
};

// Trace colours that don't read well on a dark background, and their dark-mode replacements
const DARK_TRACE_COLORS = {
  [BRAND]: CHART_COLORS.dark.brand,
  "#E2E8F0": "#334155",
};

export const chartColors = (mode) => CHART_COLORS[mode] ?? CHART_COLORS.light;

export const basePlotLayout = (yTitle, tickSuffix, mode = "light") => {
  const c = chartColors(mode);
  return {
    autosize: true,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    font: { family: "Roboto, Helvetica, Arial, sans-serif", size: 11, color: c.text },
    margin: { l: 8, r: 8, t: 8, b: 32 },
    hovermode: "x unified",
    hoverlabel: {
      bgcolor: c.hoverBg,
      bordercolor: c.hoverBorder,
      font: { color: c.hoverText, size: 12 },
    },
    legend: {
      orientation: "h",
      x: 1,
      y: 1.02,
      xanchor: "right",
      yanchor: "bottom",
      bgcolor: "rgba(0,0,0,0)",
      font: { size: 12, color: c.legend },
    },
    xaxis: {
      showline: false,
      showgrid: false,
      zeroline: false,
      ticks: "",
      nticks: 6,
    },
    yaxis: {
      title: { text: yTitle, font: { size: 12, color: c.text }, standoff: 8 },
      showline: false,
      showgrid: true,
      gridcolor: c.grid,
      zeroline: true,
      zerolinecolor: c.zeroLine,
      automargin: true,
      ticksuffix: tickSuffix,
    },
  };
};

export const basePlotConfig = {
  displayModeBar: false,
  responsive: true,
};

// Swap trace colours for their dark-mode versions (marker, marker outline and line)
const themeTraceFor = (mode) => (trace) => {
  if (mode !== "dark" || !trace) return trace;
  const swap = (color) => DARK_TRACE_COLORS[color] ?? color;
  return {
    ...trace,
    ...(trace.marker && {
      marker: {
        ...trace.marker,
        color: swap(trace.marker.color),
        ...(trace.marker.line && { line: { ...trace.marker.line, color: swap(trace.marker.line.color) } }),
      },
    }),
    ...(trace.line && { line: { ...trace.line, color: swap(trace.line.color) } }),
  };
};

// Chart colours for the current light / dark mode
export const useChartTheme = () => {
  const { mode } = useColorMode();
  return {
    mode,
    colors: chartColors(mode),
    baseLayout: (yTitle, tickSuffix) => basePlotLayout(yTitle, tickSuffix, mode),
    themeTrace: themeTraceFor(mode),
  };
};
